import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  PhoneCall,
  PhoneOff,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { agentsApi } from "@/features/agents/api";
import {
  phoneNumbersApi,
  outboundCallsApi,
  type OutboundCall,
} from "@/features/telephony/api";

/**
 * Dialog to place an outbound call.
 *
 * Two phases:
 *  1. A form to choose the agent / number to dial.
 *  2. A live status view that tracks the call in real time (dialing → in call →
 *     ended/failed) with an End Call control, so the user never has to guess
 *     what happened — a provider failure shows up here in plain language.
 */
export default function PlaceCallDialog({
  open,
  onOpenChange,
  lockedAgentId,
  lockedAssistantId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lockedAgentId?: string;
  lockedAssistantId?: string;
}) {
  const qc = useQueryClient();
  const [agentId, setAgentId] = useState(lockedAgentId ?? "");
  const [fromId, setFromId] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [context, setContext] = useState("");
  // Once a call is placed we switch to the live view keyed by this id.
  const [placedId, setPlacedId] = useState<string | null>(null);

  const agentsQ = useQuery({
    queryKey: ["agents", "list"],
    queryFn: agentsApi.list,
    staleTime: 60_000,
    enabled: !lockedAgentId && !lockedAssistantId,
  });
  const numbersQ = useQuery({
    queryKey: ["phone-numbers", "list"],
    queryFn: phoneNumbersApi.list,
    staleTime: 15_000,
  });

  const agents = agentsQ.data ?? [];
  const numbers = (numbersQ.data ?? []).filter((n) => n.status === "active");
  const effectiveAgentId = lockedAgentId ?? agentId;

  const place = useMutation({
    mutationFn: () =>
      outboundCallsApi.create({
        agent: lockedAssistantId ? null : effectiveAgentId,
        assistant: lockedAssistantId ?? null,
        from_number: fromId || null,
        to_number: toNumber.trim(),
        context: context.trim(),
      }),
    onSuccess: (res: OutboundCall) => {
      setPlacedId(res.id);
      qc.invalidateQueries({ queryKey: ["calls", "list"] });
    },
    onError: (err) => {
      const e = err as {
        response?: { data?: { error?: string; to_number?: string[] } };
      };
      toast.error(
        e?.response?.data?.to_number?.[0] ||
          e?.response?.data?.error ||
          "Failed to place call.",
      );
    },
  });

  const toValid = toNumber.trim().startsWith("+");
  const canSubmit =
    (!!lockedAssistantId || !!effectiveAgentId) && toValid && !place.isPending;

  function reset() {
    setPlacedId(null);
    setToNumber("");
    setContext("");
    if (!lockedAgentId) setAgentId("");
    setFromId("");
    place.reset();
  }

  function handleOpenChange(v: boolean) {
    if (!v) reset();
    onOpenChange(v);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        {placedId ? (
          <LiveCall
            outboundId={placedId}
            toNumber={toNumber}
            onClose={() => handleOpenChange(false)}
            onRedial={reset}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Place Outbound Call</DialogTitle>
              <DialogDescription>
                {lockedAssistantId
                  ? "The assistant will call the number you enter."
                  : "The agent will call the number you enter."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {!lockedAgentId && !lockedAssistantId && (
                <div className="space-y-1.5">
                  <Label>Agent</Label>
                  <Select value={agentId} onValueChange={setAgentId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Call from</Label>
                <Select value={fromId} onValueChange={setFromId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a number" />
                  </SelectTrigger>
                  <SelectContent>
                    {numbers.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {(n.formatted_number || n.number) +
                          " · " +
                          (n.provider || "")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Pick a Telnyx number to use voicemail detection.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="to-number">Call to (E.164)</Label>
                <Input
                  id="to-number"
                  value={toNumber}
                  onChange={(e) => setToNumber(e.target.value)}
                  placeholder="+14155551234"
                  className="font-mono"
                />
                {toNumber.trim() && !toValid && (
                  <p className="text-xs text-rose-600">
                    Must be E.164 format, e.g. +14155551234
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="call-context">Note (optional)</Label>
                <Input
                  id="call-context"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="e.g. Follow-up on quote #1024"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button disabled={!canSubmit} onClick={() => place.mutate()}>
                {place.isPending ? "Placing…" : "Place Call"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

type Phase = "queued" | "dialing" | "in_call" | "ended" | "failed" | "cancelled";

function derivePhase(oc?: OutboundCall): Phase {
  if (!oc) return "queued";
  if (oc.status === "cancelled") return "cancelled";
  if (oc.status === "failed" || oc.call?.status === "failed") return "failed";
  const cs = oc.call?.status;
  if (cs === "completed" || cs === "missed" || cs === "voicemail") return "ended";
  if (cs === "in_progress") return "in_call";
  if (cs === "pending") return "dialing";
  if (oc.status === "completed") return "ended";
  return "queued";
}

const PHASE_META: Record<
  Phase,
  { label: string; hint: string; live: boolean }
> = {
  queued: { label: "Queued", hint: "Preparing to dial…", live: true },
  dialing: { label: "Dialing…", hint: "Ringing the number.", live: true },
  in_call: { label: "In call", hint: "The agent is talking now.", live: true },
  ended: { label: "Call ended", hint: "The call has completed.", live: false },
  failed: { label: "Call failed", hint: "", live: false },
  cancelled: { label: "Cancelled", hint: "The call was cancelled.", live: false },
};

function LiveCall({
  outboundId,
  toNumber,
  onClose,
  onRedial,
}: {
  outboundId: string;
  toNumber: string;
  onClose: () => void;
  onRedial: () => void;
}) {
  const q = useQuery({
    queryKey: ["outbound-call", outboundId],
    queryFn: () => outboundCallsApi.get(outboundId),
    enabled: !!outboundId,
    refetchInterval: (query) => {
      const phase = derivePhase(query.state.data);
      // Stop polling once the call reaches a terminal state.
      return phase === "ended" || phase === "failed" || phase === "cancelled"
        ? false
        : 2000;
    },
  });

  const oc = q.data;
  const phase = derivePhase(oc);
  const meta = PHASE_META[phase];
  const canHangup = !!oc?.call?.can_hangup;
  const failureReason =
    oc?.call?.error_message ||
    oc?.error_message ||
    "The call couldn't be completed. Please try again.";

  const hangup = useMutation({
    mutationFn: () => outboundCallsApi.hangup(outboundId),
    onSuccess: () => toast.success("Ending call…"),
    onError: () => toast.error("Couldn't end the call."),
  });

  const Icon =
    phase === "failed"
      ? XCircle
      : phase === "ended"
        ? CheckCircle2
        : phase === "in_call"
          ? PhoneCall
          : Loader2;

  const tone =
    phase === "failed"
      ? "text-rose-600"
      : phase === "ended"
        ? "text-emerald-600"
        : phase === "in_call"
          ? "text-emerald-600"
          : "text-sky-600";

  return (
    <>
      <DialogHeader>
        <DialogTitle>Outbound Call</DialogTitle>
        <DialogDescription className="font-mono">
          {toNumber || oc?.to_number}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col items-center gap-3 py-6">
        <Icon
          className={`h-10 w-10 ${tone} ${meta.live ? "animate-spin" : ""}`}
          strokeWidth={1.75}
        />
        <div className="text-center">
          <div className={`text-lg font-semibold ${tone}`}>{meta.label}</div>
          <p className="mt-1 text-sm text-muted-foreground">
            {phase === "failed" ? failureReason : meta.hint}
          </p>
          {phase === "ended" && !!oc?.call?.duration_seconds && (
            <p className="mt-1 text-xs text-muted-foreground">
              Duration {formatDuration(oc.call.duration_seconds)}
            </p>
          )}
        </div>
      </div>

      <DialogFooter className="sm:justify-between">
        {canHangup ? (
          <Button
            variant="destructive"
            onClick={() => hangup.mutate()}
            disabled={hangup.isPending}
          >
            <PhoneOff className="mr-2 h-4 w-4" />
            {hangup.isPending ? "Ending…" : "End Call"}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          {(phase === "failed" || phase === "ended" || phase === "cancelled") && (
            <Button variant="outline" onClick={onRedial}>
              New Call
            </Button>
          )}
          <Button variant={meta.live ? "outline" : "default"} onClick={onClose}>
            {meta.live ? "Close" : "Done"}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function formatDuration(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}
